/** @fileoverview Renders chat messages, streamed activity, reasoning, and feedback actions. */

import { ThumbsDown, ThumbsUp } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import {
  Message,
  MessageAction,
  MessageActions,
  MessageContent,
  MessageResponse,
} from '../ai-elements/message';
import { Reasoning, ReasoningContent, ReasoningTrigger } from '../ai-elements/reasoning';

import ChatMessageTimeline from './ChatMessageTimeline';
import MessageAttachments from './MessageAttachments';
import LiveStatus from './LiveStatus';
import { getMessageActivity } from './messageActivity';
import type { ChatMessageProps } from './types';
import type { AskQuestionData, FabrikUIMessage } from '../../types/chat';
import { annotationsFromSelection, type SelectionAnnotation } from '../../lib/previewSelection';

type AssistantContentItem =
  | { kind: 'text'; key: string; text: string }
  | { kind: 'questions'; key: string; groupId?: string; questions: AskQuestionData[] };

const getAssistantContent = (
  message: FabrikUIMessage,
  questions: AskQuestionData[]
): AssistantContentItem[] => {
  const questionsById = new Map(questions.map((question) => [question.id, question]));
  const seenQuestions = new Set<string>();
  const items: AssistantContentItem[] = [];
  let text = '';
  let textKey = '';
  const flushText = () => {
    if (text) items.push({ kind: 'text', key: textKey, text });
    text = '';
    textKey = '';
  };

  message.parts.forEach((part, index) => {
    if (part.type === 'text') {
      if (part.text) {
        if (!text) textKey = `text-${index}`;
        text += text ? `\n${part.text}` : part.text;
      }
      return;
    }
    if (part.type !== 'data-ask-question') return;

    const question = questionsById.get(part.data.id);
    if (
      !question ||
      question.phase === 'pending' ||
      question.workflowApproval ||
      seenQuestions.has(question.id)
    ) {
      return;
    }
    seenQuestions.add(question.id);
    flushText();

    const previous = items.at(-1);
    if (
      previous?.kind === 'questions' &&
      (previous.groupId === question.groupId || (!previous.groupId && !question.groupId))
    ) {
      previous.questions.push(question);
    } else {
      items.push({
        kind: 'questions',
        key: `questions-${question.id}`,
        groupId: question.groupId,
        questions: [question],
      });
    }
  });
  flushText();
  return items;
};

/** The focus icon (Tabler "focus-2"): a crosshair, i.e. a targeted spot. */
function FocusIcon({ className }: { className?: string }) {
  return (
    <svg
      aria-hidden="true"
      className={className}
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
    >
      <circle cx="12" cy="12" r=".5" fill="currentColor" />
      <path d="M12 12m-7 0a7 7 0 1 0 14 0a7 7 0 1 0 -14 0" />
      <path d="M12 3l0 2" />
      <path d="M3 12l2 0" />
      <path d="M12 19l0 2" />
      <path d="M19 12l2 0" />
    </svg>
  );
}

/** One row per selection, each with its own icon tile and split from the next by a hairline. */
function SelectionAnnotations({ annotations }: { annotations: SelectionAnnotation[] }) {
  return (
    <div className="flex min-w-0 flex-col divide-y divide-app-border-strong">
      {annotations.map((annotation) => (
        <div
          className="flex min-w-0 items-start gap-2 py-1.5 first:pt-0 last:pb-0"
          key={annotation.group}
        >
          <span className="flex size-5 shrink-0 items-center justify-center text-app-text-muted">
            <FocusIcon className="h-4 w-4" />
          </span>
          <span
            className={`min-w-0 flex-1 leading-5 ${annotation.note ? '' : 'text-app-text-muted'}`}
          >
            {annotation.note || 'Selection'}
          </span>
        </div>
      ))}
    </div>
  );
}

function QuestionResults({ questions }: { questions: AskQuestionData[] }) {
  return (
    <div className="w-full min-w-0 rounded-xl border border-app-border-strong bg-app-surface-card px-3 py-2.5">
      <div className="flex flex-col gap-3">
        {questions.map((question) => (
          <div className="min-w-0" key={question.id}>
            {question.title && (
              <p className="mb-0.5 text-sm font-medium text-app-text-secondary">{question.title}</p>
            )}
            <p className="text-sm text-app-text-muted">{question.question}</p>
            {question.answer ? (
              <p className="mt-0.5 text-sm text-app-text">{question.answer}</p>
            ) : question.phase === 'failed' ? (
              <p className="mt-0.5 text-sm text-app-danger">This question could not be answered.</p>
            ) : (
              question.phase === 'expired' && (
                <p className="mt-0.5 text-sm text-app-text-muted">
                  This question timed out and can no longer be answered.
                </p>
              )
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function ChatMessage({
  message,
  savedMessage,
  busy,
  lastMessageId,
  hideActivity = false,
  onRate,
}: ChatMessageProps) {
  const text = message.parts
    .filter((part) => part.type === 'text')
    .map((part) => part.text)
    .join('');
  const reasoning = message.parts
    .filter((part) => part.type === 'reasoning')
    .map((part) => part.text)
    .join('');
  const activeContentPart = [...message.parts]
    .reverse()
    .find(
      (part) => (part.type === 'text' || part.type === 'reasoning') && part.state === 'streaming'
    );

  const isStreaming =
    !hideActivity && busy && message.role === 'assistant' && message.id === lastMessageId;
  const { activity, entries, progressLabel, questions, pendingQuestions, toolEntries, timeline } =
    getMessageActivity(message, isStreaming);
  const annotations =
    message.role === 'user'
      ? annotationsFromSelection(
          message.parts.flatMap((part) => (part.type === 'data-preview-selection' ? part.data : []))
        )
      : [];
  const attachments =
    message.role === 'user'
      ? message.parts.flatMap((part) => (part.type === 'data-attachment' ? [part.data] : []))
      : [];
  const responseItems = message.role === 'assistant' ? getAssistantContent(message, questions) : [];
  const waitingForAnswer = pendingQuestions.length > 0;
  const showActivity = !hideActivity && (isStreaming || Boolean(reasoning) || entries.length > 0);
  // Taken on first render; the streaming message mounts when its turn starts.
  const [startedAt] = useState(() => Date.now());
  const reasoningActive = !hideActivity && (isStreaming || waitingForAnswer);
  const [reasoningOpen, setReasoningOpen] = useState(false);
  const wasReasoningActive = useRef(reasoningActive);

  useEffect(() => {
    if (wasReasoningActive.current && !reasoningActive) setReasoningOpen(false);
    wasReasoningActive.current = reasoningActive;
  }, [reasoningActive]);

  const hasVisibleContent =
    message.role === 'assistant'
      ? responseItems.length > 0
      : Boolean(text) || annotations.length > 0 || attachments.length > 0;
  if (!hasVisibleContent && !showActivity) return null;
  // Attachments only: no empty bubble under them.
  if (attachments.length > 0 && !text && annotations.length === 0) {
    return (
      <Message className="min-w-0 max-w-4xl" from={message.role}>
        <MessageAttachments attachments={attachments} />
      </Message>
    );
  }

  return (
    <Message
      className={message.role === 'assistant' ? 'w-full min-w-0 max-w-full' : 'min-w-0 max-w-4xl'}
      from={message.role}
    >
      {attachments.length > 0 && <MessageAttachments attachments={attachments} />}
      <MessageContent
        className={
          message.role === 'assistant'
            ? 'w-full min-w-0 max-w-full rounded-none bg-transparent px-0 py-0 text-app-text'
            : 'min-w-0 wrap-anywhere'
        }
      >
        {showActivity && (
          <Reasoning
            className="mt-1 mb-0 w-full min-w-0 max-w-full px-0 py-0"
            defaultOpen={false}
            isStreaming={isStreaming}
            open={reasoningOpen}
            onOpenChange={setReasoningOpen}
          >
            <ReasoningTrigger>
              <LiveStatus
                entries={entries}
                progressLabel={progressLabel}
                isWriting={activeContentPart?.type === 'text'}
                isReasoning={activeContentPart?.type === 'reasoning'}
                waitingForAnswer={waitingForAnswer}
                isStreaming={isStreaming}
                completedLabel={toolEntries.length ? 'Worked' : 'Thought'}
                durationMs={activity?.durationMs}
                startedAt={startedAt}
              />
            </ReasoningTrigger>
            <ReasoningContent className="mt-1 w-full min-w-0 max-w-full pl-6 text-app-text-muted">
              <ChatMessageTimeline items={timeline} />
            </ReasoningContent>
          </Reasoning>
        )}
        {message.role === 'assistant'
          ? responseItems.map((item) =>
              item.kind === 'questions' ? (
                <QuestionResults key={item.key} questions={item.questions} />
              ) : (
                <MessageResponse
                  className="prose prose-sm min-w-0 max-w-full space-y-2 wrap-anywhere dark:prose-invert [&_pre]:max-w-full [&_pre]:overflow-x-auto [&_h1]:my-3 [&_h2]:my-3 [&_h2]:text-base [&_h3]:my-2 [&_h3]:text-sm [&_h4]:my-2 [&_p]:my-2 [&_p]:leading-5 [&_p]:whitespace-pre-line [&_ul]:my-2 [&_ol]:my-2 [&_li]:my-0.5 [&_li]:leading-5 [&_li]:whitespace-pre-line [&_hr]:my-3"
                  isAnimating={isStreaming}
                  key={item.key}
                >
                  {item.text}
                </MessageResponse>
              )
            )
          : text}
        {annotations.length > 0 && <SelectionAnnotations annotations={annotations} />}
        {savedMessage &&
          message.role === 'assistant' &&
          !busy &&
          !waitingForAnswer &&
          savedMessage.message.status !== 'waiting_for_answer' && (
            <MessageActions>
              <MessageAction
                className="text-app-text-muted hover:text-app-brand"
                label="Mark answer helpful"
                aria-label="Mark answer helpful"
                onClick={() => onRate(savedMessage, 'positive')}
              >
                <ThumbsUp className="h-3.5 w-3.5" aria-hidden="true" />
              </MessageAction>
              <MessageAction
                className="text-app-text-muted hover:text-app-danger"
                label="Mark answer unhelpful"
                aria-label="Mark answer unhelpful"
                onClick={() => onRate(savedMessage, 'negative')}
              >
                <ThumbsDown className="h-3.5 w-3.5" aria-hidden="true" />
              </MessageAction>
            </MessageActions>
          )}
      </MessageContent>
    </Message>
  );
}
